<template>
  <a :href="getAppLinkURL" class="router-link">
    <div class="slideshow lazyload" :style="getBackground">
      <img
        :id="id"
        ref="img"
        :key="`slideshowImage${id}`"
        :style="getImageStyle"
        :src="thumbURL"
        :data-src="imgURL"
        :data-thumb="thumbURL"
        :data-status="imageStatus"
        :data-io="{}"
        :data-nw="0"
        :data-nh="0"
        :alt="title"
      >

      <div v-if="!!content" class="slideshow__content">
        <div v-if="!!content.overlay" class="slideshow__content--overlay" @click.prevent.stop="onOverlayClicked">
          <img v-if="!!content.overlay" class="gallery-icon lazyload" src="../../assets/img/src/icons/gallery-icon.svg" alt="View Gallery" width="50px" height="34px">
          <div v-if="!!content.overlay" class="number">+ {{ content.count }}</div>
        </div>

        <div v-if="!!content.title || !!content.text || !!content.cta" class="slideshow__square">
          <div>
            <p v-if="content.category" ref="contentCategory" class="slideshow__copy--category uppercase" v-html="content.category" />
            <div v-if="!!content.title" ref="contentTitle" class="slideshow__copy--title" v-html="content.title" />
          </div>
          <div>
            <div v-if="!!content.text" ref="contentText" class="slideshow__copy--text" v-html="content.text" />
            <div v-if="!!content.cta" ref="contentCta" class="slideshow__copy--link btn-secondary-dark-sm">
              <div class="slideshow__copy--link-text" v-html="content.cta" />
            </div>
          </div>
        </div>

      </div>
    </div>
  </a>
</template>

<script>

export default {
  name: 'PromoSliderItem',
  props: {
    id: { type: [Number, String] },
    imgURL: { type: String, required: true },
    thumbURL: { type: String, default: '', required: false },
    title: { type: String, default: '', required: true },
    content: { type: Object, default: null },
    useBackroundImage: { type: Boolean, default: false },
    bgPosition: { type: String, default: 'center center' },
    animateContent: { type: Boolean, default: false },
  },
  data() {
    return {
      imageLoaded: false,
      imageError: false,
      thumbImage: this.thumbURL,
      status: 'not-loaded-yet',
    };
  },
  computed: {
    imageStatus() {
      return this.status;
    },
    getBackground() {
      return {
        backgroundImage: this.useBackroundImage ? `url("${encodeURI(this.imgURL)}")` : null,
        backgroundPosition: `${this.bgPosition}`,
      };
    },
    getImageStyle() {
      return {
        opacity: this.useBackroundImage ? '0' : '1',
      };
    },
    getAppLinkURL() {
      const link = !!this.content && this.content.link && this.content.linkType
        ? this.getAppLinkURLByIDandType(this.content.link, this.content.linkType)
        : !!this.content && this.content.link && !this.content.linkType
          ? this.content.link
          : null;
      return link;
    },
  },
  mounted() {
    this.$refs.img.addEventListener('load', this.onImageLoaded);
    this.$refs.img.addEventListener('error', this.onImageError);

    if (this.id === 0 || this.id === 1 || this.id === 2) {
      this.$refs.img.src = this.imgURL;
    }
  },
  methods: {
    onImageLoaded(e) {
      this.imageLoaded = true;
      this.imageError = false;
      this.status = 'loaded';
      this.$emit('ssimageloaded', e, this.status);
    },

    onImageError(e) {
      this.imageLoaded = false;
      this.imageError = true;
      this.status = 'error';
      this.$emit('ssimageloadError', e, this.status);
    },

    getAppLinkURLByIDandType(pageID, pageType = 'page') {
      return this.$store.getters['applinks/getLink'](pageType, pageID) ? this.$store.getters['applinks/getLink'](pageType, pageID).url : '';
    },

    onOverlayClicked(e) {
      e.preventDefault();
      e.stopPropagation();
      this.$emit('overlayClicked', this.id);
    },

    // Public methods accessed outside of component
    resetContentAnimation() {
      if (this.animateContent) {
        TweenMax.set(this.$refs.contentTitle, { css: { opacity: 0, top: '50px' } });
        TweenMax.set(this.$refs.contentText, { css: { opacity: 0, top: '50px' } });
        TweenMax.set(this.$refs.contentCta, { css: { opacity: 0, top: '50px' } });
      }
    },

    startContentAnimation() {
      if (this.animateContent) {
        const tweenTime = 0.6;
        const step = 0.2;
        let dtime = 0;
        TweenMax.to(this.$refs.contentTitle, tweenTime, { css: { opacity: 1, top: '0px' }, delay: dtime, ease: Power4.easeOut });
        TweenMax.to(this.$refs.contentText, tweenTime, { css: { opacity: 1, top: '0px' }, delay: dtime += step, ease: Power4.easeOut });
        TweenMax.to(this.$refs.contentCta, tweenTime, { css: { opacity: 1, top: '0px' }, delay: dtime += step, ease: Power4.easeOut });
      }
    },

  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/styles/settings/config.scss";

//PLEASE DO NOT EDIT ANYTHING and add random heights etc as this is all calculated in the slideshow
.slideshow {
    position: relative;
    width: 100%;
    height: auto;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
    background-color: $black;

    //--- For Home and results slide shows - extra feature added --//
    &__content {
        position: absolute;
        padding: 40px 80px;
        text-align: center;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;

        @media screen and (max-width: 480px)  {
          padding: 1em 2em;
          justify-content: center;
        }

        &--overlay {
          position: absolute;
          top: 0px;
          left: 0px;
          background-color: rgba(0, 0, 0, 0.6);
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          cursor: pointer;
          .gallery-icon {
            margin: 0 auto;
            cursor: pointer;
          }
          .number {
            text-align: center;
            font-size: 1.5em;
            color: $stoneWhite;
            cursor: pointer;
          }
        }
    }
    &__square {
      background-color: white;
      color: $darkBlack;
      height: 100%;
      max-width: 480px;
      display: flex;
      justify-content: space-around;
      flex-direction: column;
      padding: 2em;
      @media screen and (max-width: 480px)  {
        padding: 1em 2em;
        max-height: 450px;
      }
    }
    &__copy {
      position: absolute;
      padding: 2em;
      text-align: left;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: flex-end;
      flex-direction: column;
      line-height: 1.2;
      color: $darkBlack;
      @media screen and (max-width: 768px)  {
        padding: 28px 30px;
      }
      &--title {
        position: relative;
        font-family: $font-family-editorial;
        font-size: 40px;
        line-height: 1;
        margin-bottom: 10px;
        @media screen and (max-width: 768px)  {
          font-size: 24px;
        }
      }

      &--text {
        position: relative;
        font-size: 16px;
        font-weight: 400;
        margin-bottom: 1em;
        @media screen and (max-width: 480px) {
          font-size: 14px;
        }
      }

      &--link {
        // width: fit-content;
        // min-width: 160px;
        // margin: 0;
        // font-size: 14px;
        // color: $darkBlack;
      }
    }
}

</style>
